<template>
  <PremiumMetricWidgetBase :widget="widget" @metric-loaded="handleMetricLoaded" :state="state">
    <div class="widget__content-center" v-if="allZones.length > 0">
      <div class="widget-zone-overlap__controls">
        <div class="widget-zone-overlap__controls-ordered-overlap">
          <ToggleSwitch v-model="showOrderedZoneOverlap" />
          <p>Ordered Zone-to-Zone Crossover <span> (Show overlap in order of visits) </span></p>
        </div>
        <Button label="Add/Remove Zones" severity="secondary" @click="openZoneModal" />
      </div>
      <table class="widget-zone-overlap__table">
        <thead>
          <tr>
            <th><strong>Starting Zone</strong></th>
            <th v-for="zone_top in zones" :key="zone_top">{{ zone_top }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="zone_side in zones" :key="zone_side">
            <td>{{ zone_side }}</td>
            <td v-for="zone_top in zones" :key="zone_top">
              <span v-if="zone_top === zone_side">-</span>
              <span
                v-else
                :style="getPercentageColor(getZoneOverlapPercentage(zone_top, zone_side))"
              >
                {{ formatZoneOverlapPercentage(zone_top, zone_side) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="widget__body-empty">
      <IconInfo />
      <p>Zone overlap data is not available for this event</p>
    </div>
    <DialogFrame
      v-model:visible="zoneModalVisible"
      :loading="false"
      @onHide="closeZoneModal"
      @onClose="closeZoneModal"
      class="p-dialog-breathe"
    >
      <template #title>Add/Remove Zones</template>
      <template #body>
        <div class="widget-zone-overlap__modal-content">
          <div class="widget-zone-overlap__modal-header">
            <p>You can select a maximum of {{ MAX_ZONES }} zones</p>
          </div>
          <div class="widget-zone-overlap__zone-list">
            <div v-for="zone in allZones" :key="zone" class="widget-zone-overlap__zone-item">
              <Checkbox
                v-model="validatedTempSelectedZones"
                :value="zone"
                :inputId="`zone-${zone}`"
                :binary="false"
                :disabled="
                  validatedTempSelectedZones.length >= MAX_ZONES &&
                  !validatedTempSelectedZones.includes(zone)
                "
              />
              <label :for="`zone-${zone}`">{{ zone }}</label>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="widget-zone-overlap__modal-actions">
          <Button label="Cancel" severity="contrast" @click="cancelZoneSelection" />
          <Button label="Apply" severity="secondary" @click="applyZoneSelection" />
        </div>
      </template>
    </DialogFrame>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Button, Checkbox, ToggleSwitch } from 'primevue';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import IconInfo from '@/icons/IconInfo.svg?component';
import DialogFrame from '@/components/dialogs/DialogFrame.vue';
import { formatNumber } from '@/utils/formatNumbers';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const MAX_ZONES = 5;

type ZoneOverlap = {
  seen_at: string;
  also_seen_at: string;
  percentage: number;
  ordered_percentage: number;
};

type MetricData = {
  zone_overlaps: ZoneOverlap[];
};

defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  zone?: WidgetSetting;
}>();

const metricData = ref<MetricData | null>(null);
const zoneModalVisible = ref(false);
const selectedZones = ref<string[]>([]);
const tempSelectedZones = ref<string[]>([]);
const showOrderedZoneOverlap = ref(false);

// Computed property with setter to handle zone selection with validation
const validatedTempSelectedZones = computed({
  get: () => tempSelectedZones.value,
  set: (newZones: string[]) => {
    // Ensure we don't exceed MAX_ZONES
    tempSelectedZones.value = newZones.length > MAX_ZONES ? newZones.slice(0, MAX_ZONES) : newZones;
  },
});

const allZones = computed(() => {
  if (!metricData.value?.zone_overlaps) return [];

  return Array.from(
    new Set(metricData.value.zone_overlaps.map((zone: ZoneOverlap) => zone.seen_at)),
  ).sort();
});

const zones = computed(() => {
  return selectedZones.value.length > 0 ? selectedZones.value : allZones.value.slice(0, 5);
});

const state = computed(() => {
  if (allZones.value.length === 1) {
    return 'single-zone-disabled';
  }
  return null;
});

const handleMetricLoaded = (metric: any) => {
  metricData.value = metric.data?.data;

  // Select first 5 zones on initial load if no zones are selected
  if (metricData.value?.zone_overlaps && selectedZones.value.length === 0) {
    const initialZones = allZones.value.slice(0, 5);
    selectedZones.value = initialZones;
    tempSelectedZones.value = [...initialZones];
  }
};

const getZoneOverlapPercentage = (zoneTop: string, zoneSide: string): number => {
  if (!metricData.value?.zone_overlaps) return 0;

  const overlap = metricData.value.zone_overlaps.find(
    (z: ZoneOverlap) => z.seen_at === zoneTop && z.also_seen_at === zoneSide,
  );

  if (showOrderedZoneOverlap.value) {
    return overlap?.ordered_percentage || 0;
  } else {
    return overlap?.percentage || 0;
  }
};

const formatZoneOverlapPercentage = (zoneTop: string, zoneSide: string): string => {
  const percentage = getZoneOverlapPercentage(zoneTop, zoneSide);
  return formatNumber(percentage * 100, 2, true) + '%';
};

const getPercentageColor = (percentage: number) => {
  // Convert percentage to 0-1 range
  const normalizedPercentage = Math.max(0, Math.min(1, percentage));

  // Define colors: black (0%) to bright green (100%)
  const startColor = { r: 0, g: 0, b: 0 }; // Black
  const endColor = { r: 0, g: 255, b: 0 }; // Bright green

  // Interpolate between colors
  const r = Math.round(startColor.r + (endColor.r - startColor.r) * normalizedPercentage);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * normalizedPercentage);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * normalizedPercentage);

  return {
    color: `rgb(${r}, ${g}, ${b})`,
    fontWeight: 'bold',
  };
};

const openZoneModal = () => {
  tempSelectedZones.value = [...selectedZones.value];
  zoneModalVisible.value = true;
};

const closeZoneModal = () => {
  zoneModalVisible.value = false;
};

const cancelZoneSelection = () => {
  tempSelectedZones.value = [...selectedZones.value];
  zoneModalVisible.value = false;
};

const applyZoneSelection = () => {
  selectedZones.value = [...tempSelectedZones.value];
  zoneModalVisible.value = false;
};
</script>

<style scoped lang="scss">
.widget-zone-overlap__controls {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: variables.$vantage-base-sizing-xxs;

  &-ordered-overlap {
    align-items: center;
    display: flex;
    gap: variables.$vantage-base-sizing-xxs;

    p {
      margin: 0;
      white-space: nowrap;
    }

    span {
      color: variables.$vantage-base-primitives-color-tertiary-500;
      font-size: variables.$vantage-base-sizing-xs;
    }
  }
}

.widget-zone-overlap__table {
  display: table;
  width: 100%;

  thead {
    background-color: white;
  }

  tbody {
    background-color: transparent;
  }

  tr {
    background-color: transparent;
  }

  th:first-child,
  td:first-child {
    border-right: 1px solid variables.$vantage-base-primitives-color-border-200;
  }

  td {
    background-color: transparent;
    border-top: 1px solid variables.$vantage-base-primitives-color-border-200;
  }
}

.widget-zone-overlap__modal-header {
  align-items: center;
  background-color: variables.$vantage-base-primitives-color-secondary-100;
  border-radius: variables.$vantage-base-sizing-xxs;
  display: flex;
  justify-content: space-between;
  margin-bottom: variables.$vantage-base-sizing-sm;
  padding: variables.$vantage-base-sizing-sm;

  p {
    color: variables.$vantage-base-primitives-color-tertiary-500;
    margin: 0;
  }
}

.widget-zone-overlap__zone-list {
  background-color: variables.$vantage-base-primitives-color-background-200;
  border-radius: variables.$vantage-base-sizing-xxs;
  display: grid;
  gap: variables.$vantage-base-sizing-xs;
  grid-template-columns: 1fr 1fr;
  padding: variables.$vantage-base-sizing-sm;
}

.widget-zone-overlap__zone-item {
  align-items: center;
  display: flex;
  gap: variables.$vantage-base-sizing-xxs;

  label {
    cursor: pointer;
    user-select: none;
  }
}

.widget-zone-overlap__modal-actions {
  border-top: 1px solid variables.$vantage-base-primitives-color-border-200;
  display: flex;
  gap: variables.$vantage-base-sizing-sm;
  justify-content: flex-end;
  padding-top: variables.$vantage-base-sizing-sm;
  width: 100%;
}
</style>
